import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { explain } from '@/shared/api/problem'
import { ErrorStrip } from '@/shared/ui/states'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import { listSubjects } from '@/features/dossier/api'
import { addAudience, createCampaign, listChannels, listTemplates, previewAudience,
         type Channel, type Template } from './api'
import { ChannelReady, Parts } from './components'

/**
 * Composing a send.
 *
 * ## The count moves as you type, and it is only ever a count
 *
 * `previewAudience` returns a number. There is no button anywhere on this screen that lists the
 * people a rule matches, because that list is a register export and this screen is available to
 * anybody holding `comms:compose`. The count is enough to answer the question an operator
 * actually has — "is this the right size" — and nothing more.
 *
 * ## Nothing here sends
 *
 * The last button creates a DRAFT. Resolving the audience, seeing the real reach and the real
 * cost, and approving all happen on the campaign's own page, in that order, because each step
 * makes the next one honest.
 */
export function ComposePage() {
  const navigate = useNavigate()
  const [channels, setChannels] = useState<Channel[]>([])
  const [templates, setTemplates] = useState<Template[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [displayName, setDisplayName] = useState('')
  const [purpose, setPurpose] = useState('')
  const [channelId, setChannelId] = useState('')
  const [templateId, setTemplateId] = useState('')

  const [sourceCode, setSourceCode] = useState('VOTER_SEGMENT')
  const [sourceRef, setSourceRef] = useState('')
  const [audienceLabel, setAudienceLabel] = useState('')
  const [matches, setMatches] = useState<number | null>(null)
  const [previewing, setPreviewing] = useState(false)

  useEffect(() => {
    void listChannels().then((rows) => {
      setChannels(rows)
      setChannelId((c) => c || rows.find((r) => r.active && r.configured)?.id
        || rows[0]?.id || '')
    }).catch(() => setChannels([]))
    void listTemplates().then(setTemplates).catch(() => setTemplates([]))
    void listSubjects({ size: '1' }).catch(() => undefined)
  }, [])

  const channel = channels.find((c) => c.id === channelId)
  const template = templates.find((t) => t.id === templateId)
  const usableTemplates = channel
    ? templates.filter((t) => t.channelCode === channel.channelCode && t.active)
    : templates

  async function preview() {
    if (!sourceRef.trim()) return
    setPreviewing(true)
    setError(null)
    try {
      const result = await previewAudience({
        sourceCode, sourceRef: sourceRef.trim(), definition: null,
      })
      setMatches(result.matches)
    } catch (err) {
      setError(explain(err))
      setMatches(null)
    } finally {
      setPreviewing(false)
    }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const campaign = await createCampaign({
        displayName: displayName.trim(),
        purpose: purpose.trim(),
        channelId,
        templateId,
        electionId: null,
      })
      if (sourceRef.trim()) {
        await addAudience(campaign.id, {
          sourceCode,
          sourceRef: sourceRef.trim(),
          definition: null,
          ids: null,
          label: audienceLabel.trim() || sourceRef.trim(),
        })
      }
      navigate(`/comms/campaigns/${campaign.id}`)
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Compose"
        description="Build a send. It stays a draft until somebody reads the real numbers and
                     approves it."
      />

      {error && <ErrorStrip message={error} />}

      <form onSubmit={submit} className="space-y-3">
        <Panel>
          <div className="space-y-2 p-3">
            <p className="text-[11px] font-medium">What this send is</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Call it
                </span>
                <input value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                       required placeholder="Polling day reminder — Ward 12"
                       className="h-7 rounded border border-input bg-background px-2
                                  text-[12px]" />
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Channel
                </span>
                <select value={channelId} onChange={(e) => {
                          setChannelId(e.target.value)
                          setTemplateId('')
                        }}
                        className="h-7 rounded border border-input bg-background px-2
                                   text-[12px]">
                  {channels.length === 0 && <option value="">no channel configured</option>}
                  {channels.map((c) => (
                    <option key={c.id} value={c.id} disabled={!c.active}>
                      {c.displayName}{c.configured ? '' : ' — provider not configured'}
                    </option>
                  ))}
                </select>
                {channel && (
                  <ChannelReady configured={channel.configured}
                                providerCode={channel.providerCode} />
                )}
              </label>
            </div>
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Why this send exists
              </span>
              <textarea value={purpose} onChange={(e) => setPurpose(e.target.value)} rows={2}
                        required minLength={10}
                        placeholder="Reminding registered voters in the ward of their polling
                                     station and the closing time."
                        className="rounded border border-input bg-background px-2 py-1
                                   text-[12px]" />
              <span className="text-[10px] text-muted-foreground">
                At least 10 characters. Whoever approves this reads it, and so does anybody
                asking later why these people were messaged.
              </span>
            </label>
          </div>
        </Panel>

        <Panel>
          <div className="space-y-2 p-3">
            <p className="text-[11px] font-medium">The message</p>
            <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} required
                    className="h-7 w-full rounded border border-input bg-background px-2
                               text-[12px]">
              <option value="">Choose a template</option>
              {usableTemplates.map((t) => (
                <option key={t.id} value={t.id}>{t.displayName}</option>
              ))}
            </select>
            {channel && usableTemplates.length === 0 && (
              <p className="text-[10px] text-amber-700 dark:text-amber-400">
                No template is written for {channel.channelCode}. An SMS body in an email is not
                a formatting problem — it is a different message, so the two are kept apart.
              </p>
            )}
            {template && (
              <>
                {template.subject && (
                  <p className="text-[12px] font-medium">{template.subject}</p>
                )}
                <p className="whitespace-pre-wrap rounded border border-border bg-muted/20 p-2
                              text-[12px]">{template.body}</p>
                {template.footer && (
                  <p className="text-[10px] text-muted-foreground">
                    Ends with: “{template.footer}”
                  </p>
                )}
                <Parts parts={template.estimatedParts} characters={template.body.length} />
                {template.placeholders.length > 0 && (
                  <p className="text-[10px] text-muted-foreground">
                    Fills in {template.placeholders.join(', ')} per person.
                  </p>
                )}
              </>
            )}
          </div>
        </Panel>

        <Panel>
          <div className="space-y-2 p-3">
            <p className="text-[11px] font-medium">Who it goes to</p>
            <div className="grid gap-2 sm:grid-cols-3">
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Source
                </span>
                <select value={sourceCode} onChange={(e) => {
                          setSourceCode(e.target.value)
                          setMatches(null)
                        }}
                        className="h-7 rounded border border-input bg-background px-2
                                   text-[12px]">
                  <option value="VOTER_SEGMENT">A saved voter segment</option>
                  <option value="MOBILIZER_UNIT">Mobilizers covering a unit</option>
                </select>
              </label>
              <label className="flex flex-col gap-0.5 sm:col-span-2">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  {sourceCode === 'VOTER_SEGMENT' ? 'Segment code' : 'Unit id'}
                </span>
                <input value={sourceRef} onChange={(e) => {
                         setSourceRef(e.target.value)
                         setMatches(null)
                       }}
                       className="h-7 rounded border border-input bg-background px-2
                                  text-[12px]" />
              </label>
            </div>
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Call this audience
              </span>
              <input value={audienceLabel} onChange={(e) => setAudienceLabel(e.target.value)}
                     placeholder="Registered voters, Ward 12"
                     className="h-7 rounded border border-input bg-background px-2
                                text-[12px]" />
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" disabled={previewing || !sourceRef.trim()}
                      onClick={() => void preview()}
                      className="rounded border border-border px-2 py-1 text-[11px]
                                 disabled:opacity-50">
                {previewing ? 'Counting…' : 'How many is that?'}
              </button>
              {matches != null && (
                <span className="text-[11px]">
                  <b className="tabular-nums">{matches.toLocaleString()}</b>{' '}
                  {matches === 1 ? 'person matches' : 'people match'}, inside your own scope
                </span>
              )}
              <span className="text-[10px] text-muted-foreground">
                A count, not a list. How many of them are actually reachable is settled when the
                audience is resolved on the next screen.
              </span>
            </div>
          </div>
        </Panel>

        <button type="submit"
                disabled={busy || !displayName.trim() || purpose.trim().length < 10
                          || !channelId || !templateId}
                className="rounded bg-primary px-3 py-1.5 text-[12px] font-medium
                           text-primary-foreground disabled:opacity-50">
          {busy ? 'Creating…' : 'Create the draft'}
        </button>
        <p className="text-[10px] text-muted-foreground">
          This creates a draft and sends nothing. The next screen resolves the audience and
          shows the real reach and cost before anybody can approve it.
        </p>
      </form>
    </div>
  )
}
